import type { PaymentDetails } from '@/shared/api/types'
import { formatLeft } from '@/shared/lib/hooks/useCountdown'
import { coinOf } from '@/shared/lib/currencies'
import { crypto } from '@/shared/lib/format'
import { ClockIcon } from '@/shared/ui/icons'
import { Strip } from '@/shared/ui/Strip'

/** Меньше этого времени — полоса из зелёной становится оранжевой. */
const WARN_AT_SECONDS = 10 * 60

interface TimerProps {
  secondsLeft: number
  expired: boolean
}

/**
 * Полоса времени. Три состояния из макета: зелёная, оранжевая
 * при остатке меньше десяти минут, красная когда время вышло.
 *
 * Ноль на таймере НЕ закрывает заявку — это делает сервер. Локальные
 * часы такого права не имеют: они врут, а заявка, объявленная
 * истёкшей на живых деньгах, — это ушедший плательщик.
 */
export function TimerStrip({ secondsLeft, expired }: TimerProps) {
  if (expired || secondsLeft <= 0) {
    return (
      <Strip tone="bad" icon={<ClockIcon />}>{expired ? 'Время на оплату истекло' : 'Время вышло, проверяем статус…'}
        </Strip>
    )
  }
  const warn = secondsLeft <= WARN_AT_SECONDS
  return (
    <Strip tone={warn ? 'warn' : 'ok'} icon={<ClockIcon />} aside="Время на оплату">
        {formatLeft(secondsLeft)}
      </Strip>
  )
}

interface TopUpProps {
  payment: PaymentDetails | null
  /** Остаток, посчитанный строками выше по дереву. */
  remaining: string | null
}

/**
 * Недоплата, заявка ещё открыта.
 *
 * Единственный оставшийся здесь статус: остальные состояния страницы
 * рисуют `Confirming` и `Settled`. Раньше тут был switch по всем
 * восьми статусам, и он расходился с тем, что показывала сама
 * страница, — одно и то же состояние объяснялось в двух местах
 * по-разному.
 *
 * Сумма недостачи называется числом, а не «доплатите остаток»:
 * плательщик должен видеть, сколько именно отправлять, до того как
 * начнёт искать это в реквизитах.
 */
export function TopUpStrip({ payment, remaining }: TopUpProps) {
  const ticker = payment ? coinOf(payment.currency).ticker : ''
  return (
    <Strip tone="warn" live>
      {remaining
        ? `Не хватает ${crypto(remaining)} ${ticker}`
        : 'Пришло меньше, чем нужно'}
    </Strip>
  )
}
